/** Content-free capture diagnostics. Delivery must never interrupt recording. */
export type CaptureDiagnosticName =
  | 'recording_ui_opened'
  | 'microphone_permission_denied'
  | 'recording_attempt_started'
  | 'recording_attempt_failed'
  | 'recording_save_failed'
  | 'recording_retry_requested'
  | 'recording_cancelled';

export interface CaptureDiagnosticProperties {
  capture_kind: 'voice' | 'listener';
  phase?: 'microphone' | 'room_check' | 'speech_check' | 'passage' | 'listener_clip';
  failure_code?: 'permission_denied' | 'device_unavailable' | 'recording_failed' | 'save_failed' | 'network_error' | 'unknown';
  retry_kind?: 'save' | 'record';
  cancel_kind?: 'exit' | 'keep_later' | 'discard';
}

export interface CaptureDiagnostic {
  event_name: CaptureDiagnosticName;
  idempotency_key: string;
  occurred_at: string;
  session_id: string;
  attempt_id?: string;
  properties: CaptureDiagnosticProperties;
}

export function diagnosticUuid(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function createCaptureDiagnostics(
  deliver: (event: CaptureDiagnostic) => Promise<void> | void,
  captureKind: 'voice' | 'listener',
) {
  let sessionId: string | undefined;
  let attemptId: string | undefined;
  let phase: CaptureDiagnosticProperties['phase'];
  let opened = false;
  const emit = (eventName: CaptureDiagnosticName, properties: Partial<CaptureDiagnosticProperties> = {}) => {
    try {
      sessionId ??= diagnosticUuid();
      const event: CaptureDiagnostic = {
        event_name: eventName,
        idempotency_key: diagnosticUuid(),
        occurred_at: new Date().toISOString(),
        session_id: sessionId,
        ...(attemptId ? { attempt_id: attemptId } : {}),
        properties: { capture_kind: captureKind, ...(phase ? { phase } : {}), ...properties },
      };
      // No raw errors, labels, paths, recording contents, or storage identifiers.
      void Promise.resolve(deliver(event)).catch(() => undefined);
    } catch { /* Optional diagnostics cannot break a product action. */ }
  };
  return {
    emit,
    opened() {
      if (opened) return;
      opened = true;
      emit('recording_ui_opened');
    },
    start(nextPhase: NonNullable<CaptureDiagnosticProperties['phase']>) {
      try { attemptId = diagnosticUuid(); } catch { attemptId = undefined; }
      phase = nextPhase;
      emit('recording_attempt_started');
    },
  };
}
